import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, FormControl, InputLabel,
  Select, MenuItem, Paper
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { setEmpresaTimezone, getEmpresaTimezone } from '../utils/fechaUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return String(u.company_id || '');
  } catch { return ''; }
}

export default function AdminTimezone() {
  const [timezones, setTimezones] = useState([]);
  const [tzActual, setTzActual] = useState('');
  const [tzNueva, setTzNueva] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [companyNombre, setCompanyNombre] = useState('');
  const empresaId = getEmpresaId();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [tzListRes, tzActRes] = await Promise.all([
        fetch(`${API_BASE}/empresa/timezones/lista`),
        fetch(`${API_BASE}/empresa/${empresaId}/timezone`),
      ]);
      const tzList = await tzListRes.json();
      const tzAct = await tzActRes.json();
      setTimezones(tzList.timezones || []);
      setTzActual(tzAct.timezone || 'UTC');
      setTzNueva(tzAct.timezone || 'UTC');
      setCompanyNombre(tzAct.company || '');
    } catch (e) {
      console.error(e);
      setMsg({ severity: 'error', text: 'No se pudo cargar la configuración.' });
    }
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    if (!tzNueva || tzNueva === tzActual) return;
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/empresa/${empresaId}/timezone`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone: tzNueva }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMsg({ severity: 'error', text: data.detail || 'Error al guardar.' });
      } else {
        setTzActual(data.timezone);
        setEmpresaTimezone(data.timezone);
        setMsg({ severity: 'success', text: `Zona horaria actualizada a ${data.timezone}.` });
      }
    } catch (e) {
      setMsg({ severity: 'error', text: 'Error de conexión.' });
    }
    setSaving(false);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <LanguageIcon sx={{ fontSize: 36, color: '#0891b2' }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#134e4a' }}>
            Zona horaria
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            {companyNombre}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{
        p: 3,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }}>
        <Typography sx={{ fontSize: 13, color: '#475569', mb: 2 }}>
          Define la zona horaria en la que opera tu empresa. Se usa para:
        </Typography>
        <Box component="ul" sx={{ pl: 3, color: '#64748b', fontSize: 13, mb: 3 }}>
          <li>Interpretar "hoy", "mañana", "próximo domingo" en la conversación con agencias y turistas.</li>
          <li>Mostrar las fechas de creación de reservas, vouchers y liquidaciones en hora local.</li>
          <li>Determinar el horario de salidas, cobros en muelle y recordatorios.</li>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: '#f0f9ff', borderRadius: 2 }}>
          <CheckCircleIcon sx={{ color: '#0891b2' }} />
          <Box>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>Zona horaria actual:</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#134e4a' }}>{tzActual}</Typography>
          </Box>
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Nueva zona horaria</InputLabel>
          <Select value={tzNueva} label="Nueva zona horaria" onChange={e => setTzNueva(e.target.value)}>
            {timezones.map(tz => (
              <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {msg && <Alert severity={msg.severity} sx={{ mb: 2 }}>{msg.text}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            disabled={saving || tzNueva === tzActual}
            onClick={guardar}
            sx={{
              bgcolor: '#0891b2',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': { bgcolor: '#0e7490' },
            }}
          >
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
